import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { getAdminTabs, getAdminTab, getAdminTabContent } from '../../graphql/queries';
import { reorderTabs, reorderTabItems, removeTabItem, setTabCustomOrder, shuffleTabOrder } from '../../graphql/mutation';
import { Avatar, Tag, Spin, Empty, Button } from 'antd';
import {
  HolderOutlined,
  EditOutlined,
  PlusOutlined,
  DownOutlined,
  RightOutlined,
  DeleteOutlined,
  RetweetOutlined,
} from '@ant-design/icons';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { showSnackBar } from '../../components/SnackBar/SnackBar';
import TabModal from './TabModal';
import AddContentModal from './AddContentModal';
import './Tabs.css';

const TabPill = ({ tab, selected, onSelect, onEdit }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: tab.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={`tab-pill ${selected ? 'tab-pill-selected' : ''}`}
      onClick={() => onSelect(tab.id)}
    >
      <span className="tab-pill-drag" {...listeners} onClick={(e) => e.stopPropagation()}>
        <HolderOutlined />
      </span>
      {selected && <span className="tab-pill-dot" />}
      <span className="tab-pill-label">{tab.name}</span>
      <span
        className="tab-pill-edit"
        onClick={(e) => {
          e.stopPropagation();
          onEdit(tab.id);
        }}
      >
        <EditOutlined />
      </span>
    </div>
  );
};

const CategoryRow = ({ item, name, expanded, onToggle, thumbnail, children, right }) => (
  <div>
    <div className="tab-item-row tab-item-row-readonly" style={{ cursor: 'pointer' }} onClick={onToggle}>
      <span className="tab-item-expand-icon">{expanded ? <DownOutlined /> : <RightOutlined />}</span>
      {thumbnail}
      <div className="tab-item-body">
        <div className="tab-row-title">{name}</div>
      </div>
      <Tag color="purple">Category</Tag>
      {right}
    </div>
    {expanded && children}
  </div>
);

const NestedTools = ({ tools }) =>
  tools.length === 0 ? (
    <div className="tab-item-nested">
      <Empty description="No tools in this category" image={Empty.PRESENTED_IMAGE_SIMPLE} />
    </div>
  ) : (
    <div className="tab-item-nested">
      <div className="tool-card-scroller">
        {tools.map((tool) => (
          <div key={tool.id} className="tool-card">
            <div className="tool-card-thumb">
              {tool.bannerUrl ? (
                <img src={tool.bannerUrl} alt={tool.name} />
              ) : (
                <span className="tool-card-thumb-fallback">{tool.name?.charAt(0).toUpperCase() || '?'}</span>
              )}
            </div>
            <div className="tool-card-name">{tool.name}</div>
          </div>
        ))}
      </div>
    </div>
  );

const SortableRuleItemRow = ({ item, isCategory }) => {
  const { setNodeRef, transform, transition, isDragging, attributes, listeners } = useSortable({ id: item.id });
  const [expanded, setExpanded] = useState(false);
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };

  if (isCategory) {
    return (
      <div ref={setNodeRef} style={style} {...attributes}>
        <CategoryRow
          name={item.name}
          expanded={expanded}
          onToggle={() => setExpanded((v) => !v)}
          thumbnail={
            <span className="tab-item-drag" {...listeners} onClick={(e) => e.stopPropagation()}>
              <HolderOutlined />
            </span>
          }
          right={<span className="tab-item-subcount">{(item.tools || []).length} tools</span>}
        >
          <NestedTools tools={item.tools || []} />
        </CategoryRow>
      </div>
    );
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes} className="tab-item-row">
      <span className="tab-item-drag" {...listeners}>
        <HolderOutlined />
      </span>
      <Avatar shape="square" size={44} src={item.bannerUrl} className="tab-item-thumb">
        {!item.bannerUrl && (item.name?.charAt(0).toUpperCase() || '?')}
      </Avatar>
      <div className="tab-item-body">
        <div className="tab-row-title">{item.name}</div>
      </div>
      <Tag color="geekblue">Tool</Tag>
    </div>
  );
};

// Rule-based tabs are otherwise read-only here — membership is whatever
// matches the tab's filter live. The one exception is sortMode=CUSTOM: the
// admin can drag-reorder this pool for THIS tab only (saved via
// setTabCustomOrder as pure order overrides), without touching the
// category/tool's own global or category-scoped rowNumber.
const RuleBasedContent = ({ tabId, sortMode, contentData, loading, onShuffle, shuffling }) => {
  const [expandedCategoryIds, setExpandedCategoryIds] = useState({});
  const [orderedItems, setOrderedItems] = useState([]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const [setTabCustomOrderMutation] = useMutation(setTabCustomOrder);

  const toggleCategory = (id) => setExpandedCategoryIds((prev) => ({ ...prev, [id]: !prev[id] }));

  const categories = contentData?.categories;
  const tools = contentData?.tools;
  const isCategory = !!categories;
  const rawItems = categories || tools || [];
  const isCustom = sortMode === 'CUSTOM';

  useEffect(() => {
    setOrderedItems(rawItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentData]);

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const oldIndex = orderedItems.findIndex((i) => i.id === active.id);
    const newIndex = orderedItems.findIndex((i) => i.id === over.id);
    const newOrder = arrayMove(orderedItems, oldIndex, newIndex);

    setOrderedItems(newOrder);

    try {
      await setTabCustomOrderMutation({ variables: { tabId, ids: newOrder.map((i) => i.id) } });
    } catch (error) {
      setOrderedItems(orderedItems);
      showSnackBar('error', 'Error', error.message || 'Failed to save custom order');
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (orderedItems.length === 0) return <Empty description="No content matches this tab's rule yet" />;

  if (isCustom) {
    return (
      <>
        <div className="tab-items-header">
          <span className="tab-items-header-title">
            {isCategory ? 'Categories' : 'Tools'}{' '}
            <span className="tab-items-count">{orderedItems.length} items — drag to reorder</span>
          </span>
        </div>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={orderedItems.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            {orderedItems.map((item) => (
              <SortableRuleItemRow key={item.id} item={item} isCategory={isCategory} />
            ))}
          </SortableContext>
        </DndContext>
      </>
    );
  }

  return (
    <>
      <div className="tab-items-header">
        <span className="tab-items-header-title">
          {isCategory ? 'Categories' : 'Tools'}{' '}
          <span className="tab-items-count">{orderedItems.length} items</span>
        </span>
        <Button icon={<RetweetOutlined />} onClick={onShuffle} loading={shuffling}>
          Shuffle now
        </Button>
      </div>
      {orderedItems.map((item) =>
        isCategory ? (
          <CategoryRow
            key={item.id}
            name={item.name}
            expanded={!!expandedCategoryIds[item.id]}
            onToggle={() => toggleCategory(item.id)}
            right={<span className="tab-item-subcount">{(item.tools || []).length} tools</span>}
          >
            <NestedTools tools={item.tools || []} />
          </CategoryRow>
        ) : (
          <div key={item.id} className="tab-item-row tab-item-row-readonly">
            <Avatar shape="square" size={44} src={item.bannerUrl} className="tab-item-thumb">
              {!item.bannerUrl && (item.name?.charAt(0).toUpperCase() || '?')}
            </Avatar>
            <div className="tab-item-body">
              <div className="tab-row-title">{item.name}</div>
            </div>
            <Tag color="geekblue">Tool</Tag>
          </div>
        )
      )}
    </>
  );
};

const SortableManualItemRow = ({ tabItem, contentType, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: tabItem.id,
  });
  const [expanded, setExpanded] = useState(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  const entity = contentType === 'CATEGORY' ? tabItem.category : tabItem.tool;
  if (!entity) return null;

  const removeButton = (
    <Button type="text" danger icon={<DeleteOutlined />} onClick={(e) => { e.stopPropagation(); onRemove(tabItem.id); }} />
  );

  if (contentType === 'CATEGORY') {
    return (
      <div ref={setNodeRef} style={style} {...attributes}>
        <CategoryRow
          name={entity.name}
          expanded={expanded}
          onToggle={() => setExpanded((v) => !v)}
          thumbnail={
            <span className="tab-item-drag" {...listeners} onClick={(e) => e.stopPropagation()}>
              <HolderOutlined />
            </span>
          }
          right={
            <>
              <span className="tab-item-subcount">{(entity.tools || []).length} tools</span>
              {removeButton}
            </>
          }
        >
          <NestedTools tools={entity.tools || []} />
        </CategoryRow>
      </div>
    );
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes} className="tab-item-row">
      <span className="tab-item-drag" {...listeners}>
        <HolderOutlined />
      </span>
      <Avatar shape="square" size={44} src={entity.bannerUrl} className="tab-item-thumb">
        {!entity.bannerUrl && (entity.name?.charAt(0).toUpperCase() || '?')}
      </Avatar>
      <div className="tab-item-body">
        <div className="tab-row-title">{entity.name}</div>
      </div>
      <Tag color="geekblue">Tool</Tag>
      {removeButton}
    </div>
  );
};

// Read-only row for a MANUAL+Dynamic tab — same info as SortableManualItemRow
// but not draggable, since the live-computed order would just override any
// drag anyway. Still supports removing the item (membership stays editable).
const ManualItemRowReadOnly = ({ entity, contentType, onRemove }) => {
  const [expanded, setExpanded] = useState(false);

  const removeButton = onRemove && (
    <Button type="text" danger icon={<DeleteOutlined />} onClick={(e) => { e.stopPropagation(); onRemove(); }} />
  );

  if (contentType === 'CATEGORY') {
    return (
      <CategoryRow
        name={entity.name}
        expanded={expanded}
        onToggle={() => setExpanded((v) => !v)}
        right={
          <>
            <span className="tab-item-subcount">{(entity.tools || []).length} tools</span>
            {removeButton}
          </>
        }
      >
        <NestedTools tools={entity.tools || []} />
      </CategoryRow>
    );
  }

  return (
    <div className="tab-item-row tab-item-row-readonly">
      <Avatar shape="square" size={44} src={entity.bannerUrl} className="tab-item-thumb">
        {!entity.bannerUrl && (entity.name?.charAt(0).toUpperCase() || '?')}
      </Avatar>
      <div className="tab-item-body">
        <div className="tab-row-title">{entity.name}</div>
      </div>
      <Tag color="geekblue">Tool</Tag>
      {removeButton}
    </div>
  );
};

// Manually-curated tabs: admin picks the exact set of categories or tools.
// Ordering is the same concept as RULE_BASED tabs: Custom (sortMode) means
// drag order (TabItem.sortOrder, editable here); Dynamic means a live field
// sort that the backend recomputes on every request — so this list becomes
// read-only (dragging wouldn't have any lasting effect) and is fetched via
// getAdminTabContent instead, to preview the real live order.
const ManualContent = ({ tabId, contentType, sortMode, onAddContent }) => {
  const isCustom = sortMode === 'CUSTOM';
  const [orderedItems, setOrderedItems] = useState([]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const { data, loading, refetch } = useQuery(getAdminTab, {
    variables: { id: tabId },
    fetchPolicy: 'network-only',
  });

  const { data: liveData, loading: liveLoading, refetch: refetchLive } = useQuery(getAdminTabContent, {
    variables: { tabId },
    skip: isCustom,
    fetchPolicy: 'network-only',
  });

  const [reorderTabItemsMutation] = useMutation(reorderTabItems);
  const [removeTabItemMutation] = useMutation(removeTabItem);

  useEffect(() => {
    if (data?.getAdminTab?.items) {
      setOrderedItems(data.getAdminTab.items.slice().sort((a, b) => a.sortOrder - b.sortOrder));
    }
  }, [data]);

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const oldIndex = orderedItems.findIndex((i) => i.id === active.id);
    const newIndex = orderedItems.findIndex((i) => i.id === over.id);
    const newOrder = arrayMove(orderedItems, oldIndex, newIndex);

    setOrderedItems(newOrder);

    try {
      await reorderTabItemsMutation({ variables: { tabId, ids: newOrder.map((i) => i.id) } });
    } catch (error) {
      setOrderedItems(orderedItems);
      showSnackBar('error', 'Error', error.message || 'Failed to reorder content');
    }
  };

  const handleRemove = async (itemId) => {
    try {
      await removeTabItemMutation({ variables: { id: itemId } });
      refetch();
      if (!isCustom) refetchLive();
    } catch (error) {
      showSnackBar('error', 'Error', error.message || 'Failed to remove content');
    }
  };

  const header = (count) => (
    <div className="tab-items-header">
      <span className="tab-items-header-title">
        {contentType === 'CATEGORY' ? 'Categories' : 'Tools'}{' '}
        <span className="tab-items-count">
          {count} items{isCustom ? ' — drag to reorder' : ' — ordered live, not draggable'}
        </span>
      </span>
      <Button className="tab-add-content-btn" icon={<PlusOutlined />} onClick={onAddContent}>
        Add Content
      </Button>
    </div>
  );

  if (isCustom) {
    return (
      <>
        {header(orderedItems.length)}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60 }}>
            <Spin size="large" />
          </div>
        ) : orderedItems.length === 0 ? (
          <Empty description="No content added yet" />
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={orderedItems.map((i) => i.id)} strategy={verticalListSortingStrategy}>
              {orderedItems.map((item) => (
                <SortableManualItemRow key={item.id} tabItem={item} contentType={contentType} onRemove={handleRemove} />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </>
    );
  }

  // Dynamic: display the live-resolved order (from getAdminTabContent), but
  // look up each entity's TabItem id (from getAdminTab) to still allow removal.
  const tabItemIdByEntityId = new Map(
    orderedItems.map((i) => [contentType === 'CATEGORY' ? i.categoryId : i.toolId, i.id])
  );
  const liveEntities = liveData?.getAdminTabContent?.categories || liveData?.getAdminTabContent?.tools || [];

  return (
    <>
      {header(liveEntities.length)}
      {loading || liveLoading ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Spin size="large" />
        </div>
      ) : liveEntities.length === 0 ? (
        <Empty description="No content added yet" />
      ) : (
        liveEntities.map((entity) => (
          <ManualItemRowReadOnly
            key={entity.id}
            entity={entity}
            contentType={contentType}
            onRemove={
              tabItemIdByEntityId.has(entity.id) ? () => handleRemove(tabItemIdByEntityId.get(entity.id)) : undefined
            }
          />
        ))
      )}
    </>
  );
};

const Tabs = () => {
  const [orderedTabs, setOrderedTabs] = useState([]);
  // Persisted in the URL (?tab=<id>) instead of plain state, so a browser
  // refresh, or the page re-rendering after a save/reorder, doesn't bounce
  // the admin back to the first tab — they stay on whichever one they had open.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTabId = searchParams.get('tab');
  const setSelectedTabId = (id) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id) next.set('tab', id);
        else next.delete('tab');
        return next;
      },
      { replace: true }
    );
  };
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTabId, setEditingTabId] = useState(null);
  const [addContentOpen, setAddContentOpen] = useState(false);
  const [manualContentRefreshKey, setManualContentRefreshKey] = useState(0);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const { data, loading, refetch } = useQuery(getAdminTabs, { fetchPolicy: 'network-only' });
  const [reorderTabsMutation] = useMutation(reorderTabs);

  const selectedTab = orderedTabs.find((t) => t.id === selectedTabId);
  const isManual = selectedTab?.additionMethod === 'MANUAL';

  const { data: ruleContentData, loading: ruleContentLoading, refetch: refetchRuleContent } = useQuery(
    getAdminTabContent,
    {
      variables: { tabId: selectedTabId },
      skip: !selectedTab || isManual,
      fetchPolicy: 'network-only',
    }
  );

  const [shuffleTabOrderMutation, { loading: shuffling }] = useMutation(shuffleTabOrder);

  const handleShuffle = async () => {
    try {
      await shuffleTabOrderMutation({ variables: { tabId: selectedTabId } });
      await Promise.all([refetch(), refetchRuleContent()]);
      showSnackBar('success', 'Shuffled', 'Order shuffled and saved');
    } catch (error) {
      showSnackBar('error', 'Error', error.message || 'Failed to shuffle order');
    }
  };

  useEffect(() => {
    if (data?.getAdminTabs) {
      setOrderedTabs(data.getAdminTabs);
      if (!selectedTabId && data.getAdminTabs.length > 0) {
        setSelectedTabId(data.getAdminTabs[0].id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const oldIndex = orderedTabs.findIndex((t) => t.id === active.id);
    const newIndex = orderedTabs.findIndex((t) => t.id === over.id);
    const newOrder = arrayMove(orderedTabs, oldIndex, newIndex);

    setOrderedTabs(newOrder);

    try {
      await reorderTabsMutation({ variables: { ids: newOrder.map((t) => t.id) } });
      showSnackBar('success', 'Reordered', 'Tab order saved');
    } catch (error) {
      setOrderedTabs(orderedTabs);
      showSnackBar('error', 'Error', error.message || 'Failed to reorder tabs');
    }
  };

  const openCreateModal = () => {
    setEditingTabId(null);
    setModalOpen(true);
  };

  const openEditModal = (id) => {
    setEditingTabId(id);
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const handleSaved = () => {
    setModalOpen(false);
    refetch();
  };

  const handleDeleted = () => {
    setModalOpen(false);
    if (editingTabId === selectedTabId) setSelectedTabId(null);
    refetch();
  };

  return (
    <div className="tabs-container">
      <div className="tabs-pill-row">
        <span className="tabs-pill-row-label">Tabs</span>

        {loading ? (
          <Spin size="small" />
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={orderedTabs.map((t) => t.id)} strategy={horizontalListSortingStrategy}>
              <div className="tabs-pill-list">
                {orderedTabs.map((tab) => (
                  <TabPill
                    key={tab.id}
                    tab={tab}
                    selected={tab.id === selectedTabId}
                    onSelect={setSelectedTabId}
                    onEdit={openEditModal}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}

        <div className="tab-pill tab-pill-create" onClick={openCreateModal}>
          <PlusOutlined /> Create
        </div>
      </div>

      <div className="tab-content-section">
        {!selectedTab ? (
          loading ? (
            <div style={{ textAlign: 'center', padding: 60 }}>
              <Spin size="large" />
            </div>
          ) : (
            <Empty description="No tabs yet" />
          )
        ) : isManual ? (
          <ManualContent
            key={`${selectedTabId}-${manualContentRefreshKey}`}
            tabId={selectedTabId}
            contentType={selectedTab.contentType}
            sortMode={selectedTab.sortMode}
            onAddContent={() => setAddContentOpen(true)}
          />
        ) : (
          <RuleBasedContent
            tabId={selectedTabId}
            sortMode={selectedTab.sortMode}
            contentData={ruleContentData?.getAdminTabContent}
            loading={ruleContentLoading}
            onShuffle={handleShuffle}
            shuffling={shuffling}
          />
        )}
      </div>

      <TabModal
        key={modalOpen ? editingTabId || 'create' : 'closed'}
        open={modalOpen}
        tabId={editingTabId}
        onClose={closeModal}
        onSaved={handleSaved}
        onDeleted={handleDeleted}
      />

      {isManual && (
        <AddContentModal
          open={addContentOpen}
          tabId={selectedTabId}
          contentType={selectedTab.contentType}
          onClose={() => setAddContentOpen(false)}
          onAdded={() => {
            setAddContentOpen(false);
            setManualContentRefreshKey((k) => k + 1);
          }}
        />
      )}
    </div>
  );
};

export default Tabs;
